import { useCallback, useEffect, useMemo } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { InventoryEventPageRework } from 'src/components/Listings/InventoryEventPage/InventoryEventPageRework';
import { MainFilterBar } from 'src/components/MainFilterBar';
import { ActivePosEntityProvider } from 'src/contexts/ActivePosEntityContext';
import { AdCampaignsDataContextProvider } from 'src/contexts/AdCampaignsDataContext';
import { useAppContext } from 'src/contexts/AppContext';
import {
  CatalogDataContextProvider,
  ExpandedEventData,
} from 'src/contexts/CatalogDataContext';
import { CatalogMetricsContextProvider } from 'src/contexts/CatalogMetricsContext';
import { CatalogMultiSelectionContextProvider } from 'src/contexts/CatalogMultiSelectionContext';
import { useClientLogContext } from 'src/contexts/ClientLogContext';
import { DialogProvider } from 'src/contexts/DialogContext/DialogContext';
import {
  EventHubContextProvider,
  useEventHubContext,
} from 'src/contexts/EventHubContext';
import {
  FilterQueryContextProvider,
  useFilterQueryContext,
} from 'src/contexts/FilterQueryContext';
import { ListingNotificationContextProvider } from 'src/contexts/ListingNotificationContext';
import { ListingStatusContextProvider } from 'src/contexts/ListingStatusContext';
import { MultiSelectionContextProvider } from 'src/contexts/MultiSelectionContext';
import { StopwatchLoggingContextProvider } from 'src/contexts/StopwatchLoggingContext';
import { useGetActiveAccountClientConfig } from 'src/hooks/useGetActiveAccountClientConfig';
import { useMatchMedia } from 'src/hooks/useMatchMedia';
import { useUserHasFeature } from 'src/hooks/useUserHasFeature';
import { LayoutContent } from 'src/navigations/LayoutContent';
import {
  getDefaultReturnUrl,
  queryIdSearchParam,
} from 'src/navigations/Routes/InventoryEvent/constants';
import {
  EventIdQueryParam,
  InventoryDeeplinkQueryParam,
} from 'src/utils/constants/constants';
import { removeDeepLinkUrlPartsFromRelativeUrl } from 'src/utils/deepLinkUtils';
import { EmptyListingQuery } from 'src/utils/eventQueryUtils';
import { transformData } from 'src/utils/eventWithDataUtils';
import { ListingCatalogQueryKey } from 'src/utils/inventoryUtils';
import { addListingMetrics } from 'src/utils/ticketMetricUtils';
import { EventPageLocationState } from 'src/utils/types/EventPageLocationState';
import {
  ActionOutboxEntityType,
  Feature,
  ListingClient,
  ListingDetailDataField,
  ListingDetails,
  ListingMetrics,
  ListingQuery,
  UserSetting,
} from 'src/WebApiController';

import {
  getCatalogMetrics,
  getListingsSectionalDataForEvents,
  listingQueryValueTransformFromUrl,
  listingQueryValueTransformToUrl,
  minimizeListingQueryForMetrics,
} from '../Inventory';
import { MainRoute } from '../MainRoute';
import {
  ACTIVE_EVENT_IDS_2_QUERY_PARAM,
  ACTIVE_EVENT_IDS_QUERY_PARAM,
  POS_EVENT_IDS_SEPARATOR,
  TAB_EVENT_IDS_2_QUERY_PARAM,
  TAB_EVENT_IDS_MAPPING_ID,
  TAB_EVENT_IDS_QUERY_PARAM,
  TAB_EVENT_IDS_SEPARATOR,
} from './InventoryEvent.constants';

export function InventoryEvent() {
  const [searchParams] = useSearchParams();
  const oldPosEventId = searchParams.get(queryIdSearchParam);
  const viagEventId = searchParams.get(EventIdQueryParam);

  const activeEventIds = searchParams.get(ACTIVE_EVENT_IDS_QUERY_PARAM);
  const tabEventIds = searchParams.get(TAB_EVENT_IDS_QUERY_PARAM);

  const activeEventIds2 = searchParams.get(ACTIVE_EVENT_IDS_2_QUERY_PARAM);
  const tabEventIds2 = searchParams.get(TAB_EVENT_IDS_2_QUERY_PARAM);
  const eventIdsCacheId = searchParams.get(TAB_EVENT_IDS_MAPPING_ID);

  const posEventIds = useMemo(
    // We need to make sure all the ids in the params are lowercase or else it won't be found in the CatalogDataContext
    () =>
      (tabEventIds != null
        ? activeEventIds?.toLowerCase()?.split(POS_EVENT_IDS_SEPARATOR)
        : oldPosEventId?.toLowerCase()?.split(POS_EVENT_IDS_SEPARATOR)) ?? [],
    [activeEventIds, oldPosEventId, tabEventIds]
  );

  const viagogoVirtualId = useMemo(
    // We need to make sure all the ids in the params are lowercase or else it won't be found in the CatalogDataContext
    () =>
      tabEventIds2 != null || eventIdsCacheId != null
        ? activeEventIds2?.toLowerCase()
        : viagEventId?.toLowerCase(),
    [activeEventIds2, eventIdsCacheId, tabEventIds2, viagEventId]
  );

  const initialQuery = useMemo(() => {
    if (posEventIds.length || tabEventIds?.length) {
      const oldPosEventIds = tabEventIds
        ? tabEventIds
            .split(TAB_EVENT_IDS_SEPARATOR)
            .flatMap((eids) => eids.split(POS_EVENT_IDS_SEPARATOR))
        : posEventIds;
      return { ...EmptyListingQuery, oldPosEventIds };
    } else if (viagogoVirtualId) {
      return { ...EmptyListingQuery, eventOrMappingIds: [viagogoVirtualId!] };
    }

    return { ...EmptyListingQuery };
  }, [posEventIds, tabEventIds, viagogoVirtualId]);

  if (
    !posEventIds.length &&
    !tabEventIds?.length &&
    !tabEventIds2?.length &&
    !viagogoVirtualId &&
    !eventIdsCacheId
  ) {
    return null;
  }

  return (
    <StopwatchLoggingContextProvider>
      <EventHubContextProvider>
        <FilterQueryContextProvider<ListingQuery>
          initialQuery={initialQuery}
          emptyQuery={EmptyListingQuery}
          viewModeSettingId={UserSetting.InventoryPageViewMode}
          saveQueryInUrl
          queryValueTransformToUrl={listingQueryValueTransformToUrl}
          queryValueTransformFromUrl={listingQueryValueTransformFromUrl}
        >
          <InventoryEventContent
            viagogoVirtualId={viagogoVirtualId}
            posEventIds={posEventIds}
          />
        </FilterQueryContextProvider>
      </EventHubContextProvider>
    </StopwatchLoggingContextProvider>
  );
}

const InventoryEventContent = ({
  viagogoVirtualId,
  posEventIds,
}: {
  viagogoVirtualId: string | undefined;
  posEventIds: string[];
}) => {
  const { trackError } = useClientLogContext();
  const location = useLocation();

  const { activeAccountWebClientConfig } = useAppContext();

  const hasMobileInventoryPageFeature = useUserHasFeature(
    Feature.MobileInventoryEventPage
  );
  const hasMinimalFiltersForMetricsFeature = useUserHasFeature(
    Feature.MinimalFiltersForMetrics_Inventory
  );
  const isMobileLayout =
    useMatchMedia('mobile') && hasMobileInventoryPageFeature;

  const { selectedEventVirtualId } = useEventHubContext();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { filterQuery, setFilterQuery } = useFilterQueryContext<ListingQuery>();

  useEffect(() => {
    const currentEventId = searchParams.get(EventIdQueryParam);
    if (selectedEventVirtualId && currentEventId !== selectedEventVirtualId) {
      setFilterQuery({
        ...filterQuery,
        eventOrMappingIds: [selectedEventVirtualId!],
      });
      searchParams.set(EventIdQueryParam, selectedEventVirtualId);
      navigate(`/inventory/event?${searchParams.toString()}`, {
        replace: true,
      });
    }
    // Don't want to include filterQuery to avoid re-render of the hook
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [navigate, searchParams, selectedEventVirtualId, setFilterQuery]);

  const getCatalogDataExpandedCallback = useCallback(
    (ids: string[], filterQuery: ListingQuery) => {
      return getListingsSectionalDataForEvents(
        ids,
        filterQuery,
        [
          ListingDetailDataField.Basic,
          ListingDetailDataField.Pricing,
          ListingDetailDataField.AutoPricing,
          ListingDetailDataField.InternalNotes,
        ],
        {
          activeAccountWebClientConfig,
          onError: (error) => {
            trackError(
              'ListingClient.getListingsSectionalDataForEvents',
              error,
              {
                ...filterQuery,
                eventIds: ids,
              }
            );
          },
        }
      );
    },
    [activeAccountWebClientConfig, trackError]
  );

  const onCatalogDataExpandedCallback = useCallback(
    (
      ids: string[],
      filterQuery: ListingQuery,
      currentData?: ExpandedEventData
    ) =>
      getListingsSectionalDataForEvents(
        ids,
        filterQuery,
        [ListingDetailDataField.Purchase, ListingDetailDataField.Tags],
        {
          activeAccountWebClientConfig,
          onError: (error) => {
            trackError(
              'ListingClient.getListingsSectionalDataForEvents',
              error,
              {
                ...filterQuery,
                eventIds: ids,
              }
            );
          },
        },
        currentData
      ),
    [activeAccountWebClientConfig, trackError]
  );

  // Only use value provided at the first time (should be from '/inventory')
  const returnUrl = useMemo(() => {
    const locationState = (location.state ?? {}) as EventPageLocationState;

    if (locationState.returnUrl && locationState.keepReturnUrlUnchanged) {
      return locationState.returnUrl;
    }

    const relativeUrl =
      locationState.returnUrl || getDefaultReturnUrl(viagogoVirtualId);

    return removeDeepLinkUrlPartsFromRelativeUrl(relativeUrl, [
      InventoryDeeplinkQueryParam,
      ACTIVE_EVENT_IDS_QUERY_PARAM,
      TAB_EVENT_IDS_QUERY_PARAM,
      ACTIVE_EVENT_IDS_2_QUERY_PARAM,
      TAB_EVENT_IDS_2_QUERY_PARAM,
      TAB_EVENT_IDS_MAPPING_ID,
    ]);
  }, [location.state, viagogoVirtualId]);

  const getActClientCfg = useGetActiveAccountClientConfig(
    'InventoryEvent.ListingClient.getListingByListingId'
  );

  const getActivePosEntity = useCallback(
    async (
      posEntityId: number,
      curEntity?: ListingDetails | null,
      dataToGet?: string[] | null
    ) => {
      const actAccountCfg = await getActClientCfg();

      const listingDetail = await new ListingClient(
        actAccountCfg
      ).getListingByListingId(
        posEntityId,
        curEntity ?? undefined,
        dataToGet?.map((s) => s as ListingDetailDataField) ?? []
      );

      if (listingDetail) {
        return {
          posEntityId: listingDetail.id,
          posEntity: listingDetail,
          posEntityDisplayId: listingDetail.idOnMkp,
        };
      }

      return {};
    },
    [getActClientCfg]
  );

  return (
    <CatalogDataContextProvider<ListingQuery>
      entityType={ActionOutboxEntityType.Listing}
      queryKey={ListingCatalogQueryKey}
      getCatalogDataExpanded={getCatalogDataExpandedCallback}
      onCatalogDataExpanded={onCatalogDataExpandedCallback}
      transformEventData={transformData}
    >
      <CatalogMetricsContextProvider<ListingMetrics, ListingQuery>
        queryKey="getCatalogListingMetrics"
        entityType={ActionOutboxEntityType.Listing}
        getCatalogMetrics={(c, f, r) => getCatalogMetrics(c, f, r, false)}
        addCatalogMetrics={addListingMetrics}
        additionalTransformQuery={
          hasMinimalFiltersForMetricsFeature
            ? minimizeListingQueryForMetrics
            : undefined
        }
      >
        <ActivePosEntityProvider<ListingDetails>
          entityType={ActionOutboxEntityType.Listing}
          getActivePosEntity={getActivePosEntity}
        >
          <ListingStatusContextProvider>
            <ListingNotificationContextProvider>
              <CatalogMultiSelectionContextProvider type="listing">
                <MultiSelectionContextProvider>
                  <DialogProvider>
                    <AdCampaignsDataContextProvider loadSalesDetails>
                      <LayoutContent
                        mainRoute={MainRoute.InventoryEvent}
                        routeTitle=""
                      >
                        {!isMobileLayout && (
                          <MainFilterBar
                            isEventPage
                            returnUrl={returnUrl}
                            viagVirtId={viagogoVirtualId}
                          />
                        )}
                        <InventoryEventPageRework
                          posEventIds={posEventIds}
                          viagVirtId={viagogoVirtualId}
                          returnUrl={returnUrl}
                        />
                      </LayoutContent>
                    </AdCampaignsDataContextProvider>
                  </DialogProvider>
                </MultiSelectionContextProvider>
              </CatalogMultiSelectionContextProvider>
            </ListingNotificationContextProvider>
          </ListingStatusContextProvider>
        </ActivePosEntityProvider>
      </CatalogMetricsContextProvider>
    </CatalogDataContextProvider>
  );
};
